import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getFirestore, doc, setDoc, updateDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { MAPBOX_TOKEN } from '../firebaseConfig';
import Swal from 'sweetalert2';
import './AddressModal.css';

const db = getFirestore();

const AddressPromptModal = ({ onComplete }) => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [label, setLabel] = useState('Home');
  const timeoutRef = useRef(null);

  const searchAddress = async (input) => {
    if (!input || input.length < 3 || !MAPBOX_TOKEN) return;
    setLoading(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(input)}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&types=address,place`
      );
      const data = await response.json();
      setSuggestions(data.features || []);
    } catch (error) {
      console.error('Mapbox search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedPlace(null);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => searchAddress(value), 400);
  };

  const handleSelect = (place) => {
    setQuery(place.place_name);
    setSelectedPlace(place);
    setSuggestions([]);
  };

  const handleSave = async () => {
    if (!selectedPlace || !user) return;
    const [lng, lat] = selectedPlace.center || [0, 0];
    const context = selectedPlace.context || [];
    let city = '';
    let state = '';
    let zip = '';
    context.forEach((item) => {
      if (item.id.startsWith('place.')) city = item.text;
      if (item.id.startsWith('region.')) state = item.text;
      if (item.id.startsWith('postcode.')) zip = item.text;
    });

    const addressData = {
      address: selectedPlace.place_name,
      city,
      state,
      zip,
      lat,
      lng,
      label: label || 'Home',
      isDefault: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      // Save to /users/{uid}/addresses/{autoId}
      const addressRef = doc(collection(db, 'users', user.uid, 'addresses'));
      await setDoc(addressRef, addressData);

      // Also update user doc with reference
      await updateDoc(doc(db, 'users', user.uid), {
        hasConsumerAddress: true,
        updatedAt: serverTimestamp(),
      });

      Swal.fire({
        title: 'Address Saved!',
        text: 'You can now explore local restaurants.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
      onComplete({ ...addressData, id: addressRef.id });
    } catch (error) {
      console.error('Error saving address:', error);
      Swal.fire('Error', 'Failed to save address. ' + error.message, 'error');
    }
  };

  return (
    <div className="ff-modal-overlay">
      <div className="ff-auth-modal" style={{ maxWidth: 480 }}>
        <div className="ff-auth-header">
          <h3><i className="fas fa-map-marker-alt text-orange mr-2"></i>Add Your Address</h3>
          <p className="ff-auth-subtitle">
            We need your delivery address to show nearby restaurants.
          </p>
        </div>

        <div className="ff-auth-body">
          {!MAPBOX_TOKEN && (
            <div className="alert alert-warning mb-3">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              Mapbox token not configured.
            </div>
          )}

          <div className="ff-auth-field" style={{ position: 'relative' }}>
            <label>Search Address <span className="ff-required">*</span></label>
            <div className="ff-input-wrap">
              <i className="fas fa-search ff-input-icon"></i>
              <input
                type="text"
                value={query}
                onChange={handleInputChange}
                placeholder="Start typing your address..."
                autoFocus
              />
            </div>
            {loading && (
              <div style={{ position: 'absolute', right: 14, top: 46 }}>
                <div className="ff-spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></div>
              </div>
            )}
            {suggestions.length > 0 && (
              <div className="ff-suggestions" style={{ position: 'absolute', left: 0, right: 0, zIndex: 9999 }}>
                {suggestions.map((place, index) => (
                  <div key={index} className="ff-suggestion-item" onClick={() => handleSelect(place)}>
                    <i className="fas fa-map-marker-alt"></i>
                    {place.place_name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedPlace && (
            <div className="alert alert-info mt-2">
              <i className="fas fa-check-circle mr-2"></i>
              Selected: <strong>{selectedPlace.place_name}</strong>
            </div>
          )}

          <div className="ff-auth-field mt-3">
            <label>Label</label>
            <div className="d-flex gap-2">
              {['Home', 'Work', 'Other'].map((l) => (
                <button
                  key={l}
                  type="button"
                  className={`flex-fill py-2 ${label === l ? 'font-weight-bold' : ''}`}
                  style={{
                    border: label === l ? '2px solid #ff5722' : '2px solid #e0e0e0',
                    background: label === l ? '#fff3e0' : '#fff',
                    color: label === l ? '#ff5722' : '#666',
                    borderRadius: 10,
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                  }}
                  onClick={() => setLabel(l)}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="ff-auth-footer">
          <button
            className="ff-btn ff-btn-primary ff-btn-block"
            onClick={handleSave}
            disabled={!selectedPlace}
          >
            <i className="fas fa-save mr-2"></i>Save Address & Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddressPromptModal;
